<template>
  <div class="match-card" :class="stateClass">
    <div class="match-card__stage">{{ stageBadge }}</div>
    <div class="match-card__field">
      <div class="match-card__summary" :class="{ 'is-result': isResultState }">
        <div class="team-summary" :class="teamClass(1)">
          <span class="team-summary__flag">
            <TeamLogo
              v-if="hasTeamFlag(1)"
              class="team-summary__logo"
              :src="flagUrl(1)"
              :name="teamName(1)"
            />
          </span>
          <span class="team-summary__name" :class="teamNameClass(1)">{{ teamName(1) }}</span>
        </div>
        <div class="match-card__center">
          <div v-if="isResultState" class="match-card__score">{{ resultScore }}</div>
          <div v-else class="match-card__vs">VS</div>
          <div v-if="showCenterTime" class="match-card__time">{{ formatShortDateTime(match.deadline_time) }}</div>
          <div v-if="centerPill" class="match-card__status-pill">{{ centerPill }}</div>
          <template v-else>
            <div class="match-card__time">{{ formatShortDateTime(match.deadline_time) }}</div>
            <div class="match-card__deadline">{{ centerMetaText }}</div>
          </template>
        </div>
        <div class="team-summary" :class="teamClass(2)">
          <span class="team-summary__flag">
            <TeamLogo
              v-if="hasTeamFlag(2)"
              class="team-summary__logo"
              :src="flagUrl(2)"
              :name="teamName(2)"
            />
          </span>
          <span class="team-summary__name" :class="teamNameClass(2)">{{ teamName(2) }}</span>
        </div>
      </div>

      <div v-if="showBetOptions" class="match-card__options">
        <button class="bet-option" :class="optionClass(1)" :disabled="!state.canBet" @click="$emit('choose', { match, side: 1 })">
          <span class="bet-option__title">猜{{ match.team_a || '主队' }}胜</span>
          <span class="bet-option__sub">{{ percents.a }}%用户已选</span>
          <span class="bet-option__bar">
            <span :style="{ width: `${percents.a}%` }"></span>
          </span>
        </button>
        <button class="bet-option" :class="optionClass(2)" :disabled="!state.canBet" @click="$emit('choose', { match, side: 2 })">
          <span class="bet-option__title">猜{{ match.team_b || '客队' }}胜</span>
          <span class="bet-option__sub">{{ percents.b }}%用户已选</span>
          <span class="bet-option__bar">
            <span :style="{ width: `${percents.b}%` }"></span>
          </span>
        </button>
      </div>

      <div v-if="!state.canBet" class="match-card__state">{{ state.text }}</div>
      <div v-else class="match-card__note">
        猜中可抽好礼 · 100%中奖 · <strong>门票 {{ ticketLabel }}</strong> <strong class="is-coin">默往币/人</strong>
      </div>
    </div>
  </div>
</template>

<script>
import { isResolvedTeamName, matchPercents, matchState, resolveWinnerSide, roundLabel } from '../utils'
import TeamLogo from '../../worldcup2026/components/TeamLogo.vue'

export default {
  name: 'WorldCupQuizMatchCard',
  components: { TeamLogo },
  props: {
    match: {
      type: Object,
      required: true
    },
    ticketCoin: {
      type: Number,
      default: 0
    },
    selectedSide: {
      type: Number,
      default: 0
    }
  },
  computed: {
    state () {
      return matchState(this.match)
    },
    percents () {
      return matchPercents(this.match)
    },
    stageBadge () {
      return roundLabel(this.match)
    },
    ticketLabel () {
      return this.ticketCoin || (this.match && this.match.ticket_coin) || '--'
    },
    stateClass () {
      return `is-${this.state.type}`
    },
    isResultState () {
      return this.state.type === 'win' || this.state.type === 'lose' || this.state.type === 'ended'
    },
    isPendingState () {
      return this.state.type === 'pending'
    },
    showCenterTime () {
      return this.state.type === 'closed'
    },
    showBetOptions () {
      return !this.isResultState && !this.isPendingState
    },
    centerPill () {
      if (this.isResultState) return '已完赛'
      if (this.state.type === 'running') return `进行中${this.match.live_minute ? `${this.match.live_minute}'` : ''}`
      if (this.state.type === 'closed') return '已截止'
      return ''
    },
    centerMetaText () {
      if (this.isPendingState) return '开始比赛'
      return this.state.type === 'open' ? '开赛前截止' : '等待开赛'
    },
    resultScore () {
      const score = String(this.match.result_score || '').replace('-', ' : ')
      return score || '- : -'
    },
    chosenSide () {
      return Number(this.match && this.match.my_bet && this.match.my_bet.chosen_side) || 0
    },
    winnerSide () {
      return resolveWinnerSide(this.match)
    }
  },
  methods: {
    roundLabel,
    formatShortDateTime (seconds) {
      if (!seconds) return '--'
      const date = new Date(Number(seconds) * 1000)
      const pad = n => String(n).padStart(2, '0')
      return `${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    flagUrl (side) {
      return side === 1 ? this.match.team_a_flag : this.match.team_b_flag
    },
    hasTeamFlag (side) {
      if (this.isPendingState) return ''
      return isResolvedTeamName(this.teamName(side)) && this.flagUrl(side)
    },
    teamName (side) {
      if (this.isPendingState) return '待公布'
      const name = side === 1 ? this.match.team_a : this.match.team_b
      return isResolvedTeamName(name) ? name : '待公布'
    },
    optionClass (side) {
      return {
        'is-selected': this.chosenSide === side || this.selectedSide === side,
        'is-winner': this.winnerSide === side
      }
    },
    teamClass (side) {
      return {
        'is-winner': this.isResultState && this.winnerSide === side,
        'is-chosen': this.chosenSide === side
      }
    },
    teamNameClass (side) {
      const length = this.teamName(side).length
      return {
        'is-long-name': length >= 5,
        'is-extra-long-name': length >= 7
      }
    }
  }
}
</script>

<style scoped lang="scss">
.match-card {
  position: relative;
  height: 272px;
  margin: 0 4px;
  padding-top: 10px;
  box-sizing: border-box;
}

.match-card.is-win,
.match-card.is-lose,
.match-card.is-ended {
  height: 178px;
}

.match-card.is-pending {
  height: 193px;
}

.match-card__stage {
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 28px;
  border-radius: 22px;
  color: #1e5f2a;
  background: #ffe744;
  transform: translateX(-50%);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.match-card__field {
  height: 262px;
  padding: 34px 14px 12px;
  box-sizing: border-box;
  border: 2px solid #ffe744;
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(5, 42, 18, .08), rgba(4, 32, 14, .18)),
    url('~@/assets/image/worldcupQuiz/match-card-field.png') center/cover no-repeat;
}

.match-card.is-win .match-card__field,
.match-card.is-lose .match-card__field,
.match-card.is-ended .match-card__field {
  height: 168px;
}

.match-card.is-pending .match-card__field {
  height: 183px;
  border-color: rgba(255, 231, 68, .72);
}

.match-card__summary {
  display: grid;
  grid-template-columns: 90px 1fr 90px;
  align-items: start;
  text-align: center;
}

.match-card__summary.is-result {
  align-items: center;
}

.team-summary {
  min-width: 0;
}

.team-summary__flag {
  position: relative;
  display: block;
  overflow: hidden;
  width: 58px;
  height: 58px;
  margin: 0 auto 5px;
  border-radius: 50%;
  color: #fff;
  border: 2px solid rgba(255, 255, 255, .88);
  box-sizing: border-box;
  background:
    radial-gradient(circle at 50% 50%, #f8f8f8 0 45%, #8eb5ff 46% 100%) center/cover no-repeat;
  line-height: 58px;
  font-size: 24px;
  font-weight: 600;
}

.team-summary.is-winner .team-summary__flag {
  overflow: visible;
}

.match-card.is-pending .team-summary__flag {
  border: 0;
  background: #e6e6e6;
}

.team-summary__logo {
  width: 100%;
  height: 100%;
  flex: 0 0 auto;
  border-radius: 50%;
  background: transparent;
  color: #1e5f8a;
  font-size: 18px;
  line-height: 54px;
}

.match-card.is-pending .team-summary__flag:before {
  content: "?";
  display: block;
  color: #b8b8b8;
  font-size: 36px;
  font-weight: 700;
  line-height: 58px;
}

.team-summary.is-winner .team-summary__flag:after {
  content: "";
  position: absolute;
  top: 5px;
  left: calc(100% - 7px);
  z-index: 1;
  width: 73px;
  height: 73px;
  background: url('~@/assets/image/worldcupQuiz/icon-win.png') center/contain no-repeat;
  transform: translate(-50%, -50%) rotate(16.72deg);
  transform-origin: center center;
  pointer-events: none;
}

.team-summary__name {
  display: block;
  overflow: hidden;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.team-summary__name.is-long-name {
  font-size: 12px;
}

.team-summary__name.is-extra-long-name {
  font-size: 10px;
}

.match-card__vs {
  color: #ffe744;
  text-align: center;
  font-size: 26px;
  font-weight: 600;
  line-height: 36px;
}

.match-card__score {
  color: #ffe744;
  text-align: center;
  font-size: 26px;
  font-weight: 700;
  line-height: 36px;
}

.match-card__status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 58px;
  height: 22px;
  margin-top: 4px;
  padding: 0 10px;
  box-sizing: border-box;
  border-radius: 12px;
  color: #1e5f2a;
  background: #ffe744;
  font-size: 12px;
  font-weight: 600;
  line-height: 17px;
}

.match-card.is-closed .match-card__status-pill {
  min-width: 0;
  height: auto;
  padding: 0;
  border-radius: 0;
  color: #fff;
  background: transparent;
  font-size: 14px;
  line-height: 14px;
}

.match-card__time {
  margin-top: 2px;
  color: #fff;
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
}

.match-card__deadline {
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.match-card__options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  height: 80px;
  margin-top: 17px;
}

.bet-option {
  min-width: 0;
  padding: 14px 10px 11px;
  border: 2px solid transparent;
  border-radius: 8px;
  background: rgba(7, 41, 16, .78);
  text-align: center;
}

.bet-option:disabled {
  opacity: 1;
}

.bet-option.is-selected {
  border-color: #ffe744;
  background: rgba(8, 61, 22, .88);
}

.bet-option__title,
.bet-option__sub {
  display: block;
}

.bet-option__title {
  overflow: hidden;
  color: #fff;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.bet-option.is-selected .bet-option__title {
  color: #ffe744;
}

.bet-option__sub {
  margin-top: 2px;
  color: #fff;
  font-size: 10px;
  font-weight: 400;
  line-height: 14px;
}

.bet-option__bar {
  position: relative;
  display: block;
  height: 4px;
  margin-top: 8px;
  overflow: hidden;
  border-radius: 2px;
  background: #fff;
}

.bet-option__bar span {
  display: block;
  height: 100%;
  border-radius: 2px;
  background: #ffe744;
}

.match-card__note {
  width: 264px;
  margin-top: 8px;
  margin-left: auto;
  margin-right: auto;
  color: rgba(255, 255, 255, .8);
  text-align: center;
  font-size: 12px;
  font-weight: 300;
  line-height: 1;
}

.match-card__state {
  width: 100%;
  margin-top: 8px;
  margin-left: auto;
  margin-right: auto;
  color: rgba(255, 255, 255, .82);
  text-align: center;
  font-size: 11px;
  font-weight: 400;
  line-height: 17px;
}

.match-card.is-joined .match-card__state,
.match-card.is-win .match-card__state {
  color: rgba(255, 255, 255, .9);
}

.match-card.is-lose .match-card__state,
.match-card.is-ended .match-card__state {
  color: rgba(255, 255, 255, .82);
}

.match-card__note strong {
  font-weight: 600;
}

.match-card__note strong.is-coin {
  color: #ffe744;
}

.match-card.is-pending .team-summary {
  opacity: .72;
}

.match-card.is-pending .match-card__state {
  margin-top: 17px;
  color: rgba(255, 255, 255, .82);
  font-size: 12px;
  line-height: 17px;
}

.match-card.is-running .bet-option,
.match-card.is-closed .bet-option,
.match-card.is-pending .bet-option {
  opacity: .5;
  background: #00450c;
}

.match-card.is-running .bet-option__title,
.match-card.is-closed .bet-option__title,
.match-card.is-pending .bet-option__title {
  color: rgba(255, 255, 255, .76);
}

.match-card.is-running .bet-option__sub,
.match-card.is-closed .bet-option__sub,
.match-card.is-pending .bet-option__sub {
  color: rgba(255, 255, 255, .54);
}

.match-card.is-running .bet-option__bar,
.match-card.is-closed .bet-option__bar,
.match-card.is-pending .bet-option__bar {
  background: rgba(255, 255, 255, .45);
}

.match-card.is-running .bet-option__bar span,
.match-card.is-closed .bet-option__bar span,
.match-card.is-pending .bet-option__bar span {
  background: rgba(255, 231, 68, .62);
}
</style>
